// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import numeral from 'numeral';
import {
  cond,
  constant,
  first,
  get,
  isEmpty,
  isNil,
  memoize,
  stubTrue,
  values,
  xor
} from 'lodash/fp';
import styled from '@emotion/styled';

import FlexiSelect from 'app/components/commons/FlexiSelect';
import GridSelect from 'app/components/commons/GridSelect';
import Modal from 'app/components/commons/Modal';
import FiltersToggleButton from 'app/components/commons/FiltersToggleButton';
import RectangularButton from 'app/components/commons/RectangularButton';
import PlayerList from 'app/components/playerSwap/PlayerList';
import PlayerPool from 'app/components/playerSwap/PlayerPool';
import Message from 'app/components/commons/Message';
import PlayerGamesBar from 'app/components/challenge/PlayerGamesBar';
import PlayerPositionsBar from 'app/components/challenge/PlayerPositionsBar';
import PlayerSwapConfirm from 'app/components/playerSwap/PlayerSwapConfirm';
import PlayerSwapFilterBar from 'app/components/playerSwap/PlayerSwapFilterBar';
import PlayerSwapSummary from 'app/components/playerSwap/PlayerSwapSummary';
import SearchBar from 'app/components/commons/SearchBar';
import Responsive from 'app/components/responsive/Responsive';
import SortIndicator from 'app/components/challenge/SortIndicator';
import Button from 'app/components/commons/Button';
import Spinner from 'app/components/commons/Spinner';
import playerSwapMyPlayersSelector from 'app/selectors/playerSwapMyPlayersSelector';
import playerSwapOtherPlayersSelector from 'app/selectors/playerSwapOtherPlayersSelector';
import {
  getMySlate,
  getMySlates,
  setChallengeGroup,
  setSelectedPlayer,
  setSwapPlayer
} from 'app/actions/playerSwapActions';
import withFilter from 'app/hoc/withFilter';
import withSort from 'app/hoc/withSort';
import OpenChallengeFilterContent from '../../components/challenge/OpenChallengeFilterContent';
import SwapRules from '../../components/playerSwap/SwapRules';
import { getFactoredEntityPositionSalary } from '../../selectors/salaryExpenditureSelector';

const getSalaryLoss = (
  swapPlayerId,
  otherPlayers,
  myPlayers,
  selectedPlayerId,
  selectedPositionId
) => {
  let salaryLoss = 0;

  if (swapPlayerId && otherPlayers && myPlayers) {
    const outPlayer = myPlayers.find(p => p.id === selectedPlayerId);
    const inPlayer = otherPlayers.find(p => p.id === swapPlayerId);

    if (outPlayer && inPlayer) {
      // !We don't need to multiply the salary by the factor because the out player is already multiplied in the backend
      const outPlayerFactoredSalary = outPlayer.salary;

      const inPlayerFactoredSalary = getFactoredEntityPositionSalary(
        inPlayer.salary,
        inPlayer.positions,
        selectedPositionId
      );

      salaryLoss = outPlayerFactoredSalary - inPlayerFactoredSalary;
    }
  }

  return numeral(salaryLoss).format('$0,0');
};

const isValidSalary = (selectedPlayerId, myPlayers, inPlayer, selectedPositionId) => {
  if (selectedPlayerId && myPlayers && inPlayer && selectedPositionId) {
    const outPlayer = myPlayers.find(p => p.id === selectedPlayerId);
    if (outPlayer) {
      const outPlayerFactoredSalary = outPlayer.salary;

      const inPlayerFactoredSalary = getFactoredEntityPositionSalary(
        inPlayer.salary,
        inPlayer.positions,
        selectedPositionId
      );

      return outPlayerFactoredSalary >= inPlayerFactoredSalary;
    }
  }
  return true;
};

type Props = {
  filters: Object,
  gameLabel: string,
  games: Array<*>,
  groupOptions: Array<*>,
  hasTeams: boolean,
  isRacing?: boolean,
  loadingSlate: boolean,
  loadingSlates: boolean,
  myPlayers: Array<*>,
  onFetchSlate: (id: number) => void,
  onFetchSlates: () => void,
  onFilter: (string, ?string) => void,
  onFilterReset: () => void,
  onGroupChange: string => void,
  onSelectedPlayerChange: (?string, ?string, ?string) => void,
  onSort: (string, ?boolean) => void,
  onSwapPlayerChange: string => void,
  otherPlayers: Array<any>,
  positionName: string,
  positions?: Array<any>,
  selectedGroupId?: number,
  selectedPlayerId?: string,
  selectedPosition?: string,
  showAllPositionsFilter?: boolean,
  sortDescending: boolean,
  sortField?: string,
  sportId?: number,
  submissionData: Array<any>,
  swapPlayerId?: string
};

const PlayerSwap: React.FC<Props> = ({
  filters,
  gameLabel,
  games,
  groupOptions,
  hasTeams,
  isRacing,
  loadingSlate,
  loadingSlates,
  myPlayers,
  onFetchSlates,
  onFilter,
  onGroupChange,
  onSelectedPlayerChange,
  onSort,
  onSwapPlayerChange,
  otherPlayers,
  positionName,
  positions,
  selectedGroupId,
  selectedPlayerId,
  selectedPosition,
  showAllPositionsFilter,
  sortDescending,
  sortField,
  swapPlayerId,
  onFetchSlate,
  onFilterReset,
  sportId,
  submissionData
}: Props) => {
  const [isFilterMenuOpen, setIsFilterMenuOpen] = React.useState(false);
  const [isPlayerListFiltersContentOpen, setIsPlayerListFiltersContentOpen] = React.useState(false);
  const [isPlayerPoolFiltersContentOpen, setIsPlayerPoolFiltersContentOpen] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [hideActivePlayers, setHideActivePlayers] = React.useState(false);
  const [playerListSearchTerm, setPlayerListSearchTerm] = React.useState('');
  const [playerPoolSearchTerm, setPlayerPoolSearchTerm] = React.useState('');
  const [playerListFilteredTeams, setPlayerListFilteredTeams] = React.useState([]);
  const [playerPoolFilteredTeams, setPlayerPoolFilteredTeams] = React.useState([]);
  const [showRules, setShowRules] = React.useState(false);

  const resetPlayerListFiltersContent = () => {
    setPlayerListFilteredTeams([]);
  };

  const resetPlayerPoolFiltersContent = () => {
    setPlayerPoolFilteredTeams([]);
  };

  const resetFilters = () => {
    resetPlayerListFiltersContent();
    resetPlayerPoolFiltersContent();
    setHideActivePlayers(false);
  };

  const onToggleHideActivePlayers = () => setHideActivePlayers(!hideActivePlayers);

  const togglePlayerListTeamsFilter = teamId =>
    setPlayerListFilteredTeams(xor(playerListFilteredTeams, [teamId]));

  const togglePlayerPoolTeamsFilter = teamId =>
    setPlayerPoolFilteredTeams(xor(playerPoolFilteredTeams, [teamId]));

  const handleSelectGroup = () => {
    if (selectedGroupId) {
      onFetchSlate(selectedGroupId);
    }
    onFilterReset();
    resetFilters();
  };

  const handleSelectSport = () => onGroupChange(undefined);

  const handleFetchSlates = () => onFetchSlates({ status: 'active,progressing' });

  const handleFilter = memoize((field: string) => (value: string) => {
    onFilter(field, isNil(value) ? undefined : value);
  });

  const handlePlayerReset = () => onSelectedPlayerChange(undefined, undefined, undefined);

  const handleReset = () => {
    setIsModalOpen(false);
    resetFilters();

    if (selectedGroupId) {
      onFetchSlate(selectedGroupId);
    }
  };

  const handleSort = (field: string) => {
    const defaultDesc = field !== 'name' && field !== 'homeAbbr';

    onSort(field, field === sortField ? !sortDescending : defaultDesc);
  };

  const toggleFilterMenu = () => setIsFilterMenuOpen(!isFilterMenuOpen);

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => {
    onSwapPlayerChange(undefined);
    setIsModalOpen(false);
  };

  const handleShowRules = () => setShowRules(true);

  const handleSortMyPlayers = () => {
    if (!loadingSlate) {
      if (isRacing) {
        onSort('number', false);
      } else {
        onSort('salary', true);
      }
    }
  };

  React.useEffect(handleFetchSlates, []);
  React.useEffect(handleSelectGroup, [selectedGroupId]);
  React.useEffect(handleSelectSport, [sportId]);
  React.useEffect(handleSortMyPlayers, [loadingSlate]);

  const salaryLoss = getSalaryLoss(
    swapPlayerId,
    otherPlayers,
    myPlayers,
    selectedPlayerId,
    selectedPosition
  );

  const message = cond([
    [constant(swapPlayerId), constant(isRacing ? undefined : `Salary loss: ${salaryLoss}`)],
    [constant(selectedPlayerId), constant('Select player to swap in')],
    [constant(selectedGroupId), constant('Select player to swap out')],
    [stubTrue, constant('Select slate')]
  ])();

  const filteredMyPlayers = myPlayers
    .filter(
      p =>
        !playerListSearchTerm ||
        p.name.toLowerCase().indexOf(playerListSearchTerm.toLowerCase()) !== -1
    )
    .filter(p => !hideActivePlayers || !p.included);

  const filteredOtherPlayers = otherPlayers.filter(
    p =>
      (!playerPoolSearchTerm ||
        p.name.toLowerCase().indexOf(playerPoolSearchTerm.toLowerCase()) !== -1) &&
      isValidSalary(selectedPlayerId, myPlayers, p, selectedPosition)
  );

  const showLineUps = !!selectedGroupId && myPlayers.length > 0;

  return (
    <Wrapper>
      <Responsive mobile={!!swapPlayerId} desktop={isModalOpen}>
        <Modal
          isOpen
          size="small"
          onCancel={closeModal}
          showCloseButton={submissionData.length === 0}
          shouldCloseOnOverlayClick={submissionData.length === 0}
        >
          <PlayerSwapConfirm
            isRacing={isRacing}
            onCancel={closeModal}
            onReset={handleReset}
            salaryLoss={isRacing ? undefined : salaryLoss}
          />
        </Modal>
      </Responsive>
      {showRules && (
        <Modal isOpen size="medium" onCancel={() => setShowRules(false)}>
          <SwapRules />
          <CloseButton type="button" onClick={() => setShowRules(false)}>
            Close
          </CloseButton>
        </Modal>
      )}
      <SlateRow>
        <GroupSelect
          loading={loadingSlates}
          height={40}
          onChange={onGroupChange}
          options={groupOptions}
          placeholder="Select slate"
          value={selectedGroupId}
        />
        <StyledButton autoBorderRadius={false} kind="secondary" onClick={handleShowRules}>
          <div className="buttonInner">
            <ButtonLabel>Swap Rules</ButtonLabel>
            <span className="ion-information-circled" />
          </div>
        </StyledButton>
      </SlateRow>
      {loadingSlate && <Spinner center />}
      {selectedPlayerId && (
        <SummaryPanel
          danger={!!swapPlayerId}
          isRacing={isRacing}
          message={message}
          onCancel={handlePlayerReset}
          onSubmit={openModal}
        />
      )}
      <Responsive mobile={showLineUps}>
        <CalloutText danger={!!swapPlayerId}>{message}</CalloutText>
      </Responsive>
      <Responsive mobile={showLineUps} tabletLandscape={false}>
        <React.Fragment>
          <MiniFilterBarWrapper>
            <PositionTitle>{positionName}</PositionTitle>
            <SortButton type="button" onClick={toggleFilterMenu}>
              <SortIndicator field={sortField} direction={sortDescending ? 'desc' : 'asc'} />
              <PlayerSelectionMenuIcon
                className={isFilterMenuOpen ? 'ion-chevron-up' : 'ion-chevron-down'}
              />
            </SortButton>
          </MiniFilterBarWrapper>
          {isFilterMenuOpen &&
            selectedPlayerId &&
            (isRacing ? (
              <GridSelect selectedValue={sortField} onSelect={handleSort}>
                <RectangularButton value="number">Number</RectangularButton>
                <RectangularButton value="barrier">Barrier</RectangularButton>
                <RectangularButton value="winProjected">Win</RectangularButton>
                <RectangularButton value="placeProjected">Place</RectangularButton>
                <RectangularButton value="weight">Weight</RectangularButton>
              </GridSelect>
            ) : (
              <GridSelect selectedValue={sortField} onSelect={handleSort}>
                <RectangularButton value="name">Name</RectangularButton>
                <RectangularButton value="homeAbbr">Match</RectangularButton>
                <RectangularButton value="salary">Salary</RectangularButton>
                <RectangularButton value="average">Average</RectangularButton>
                <RectangularButton value="form">Form</RectangularButton>
              </GridSelect>
            ))}
          {isFilterMenuOpen &&
            !selectedPlayerId &&
            (isRacing ? (
              <GridSelect selectedValue={sortField} onSelect={handleSort}>
                <RectangularButton value="number">Number</RectangularButton>
                <RectangularButton value="barrier">Barrier</RectangularButton>
                <RectangularButton value="entryCount">#Sel</RectangularButton>
                <RectangularButton value="winProjected">Win</RectangularButton>
                <RectangularButton value="placeProjected">Place</RectangularButton>
                <RectangularButton value="exposure">Exposure %</RectangularButton>
              </GridSelect>
            ) : (
              <GridSelect selectedValue={sortField} onSelect={handleSort}>
                <RectangularButton value="name">Name</RectangularButton>
                <RectangularButton value="homeAbbr">Match</RectangularButton>
                <RectangularButton value="entryCount">#Sel</RectangularButton>
                <RectangularButton value="salary">Salary</RectangularButton>
                <RectangularButton value="exposure">Exposure %</RectangularButton>
              </GridSelect>
            ))}
        </React.Fragment>
      </Responsive>
      <Main>
        <Responsive mobile={!selectedPlayerId} desktop>
          <Screen>
            {showLineUps && (
              <React.Fragment>
                <PlayerListFiltersArea>
                  <FilterBarWrapper>
                    <PlayerSwapFilterBar
                      onToggleHideActivePlayers={onToggleHideActivePlayers}
                      hideActivePlayers={hideActivePlayers}
                      label={isRacing ? 'Show Scratched' : 'Hide Active'}
                    />
                    <StyledSearchBar
                      placeholder="Search for player to swap out"
                      onClear={() => setPlayerListSearchTerm('')}
                      onSearch={value => setPlayerListSearchTerm(value)}
                      value={playerListSearchTerm}
                    />
                    <StyledFiltersToggleButton
                      isFiltered={playerListFilteredTeams.length > 0}
                      isOpen={isPlayerListFiltersContentOpen}
                      onClick={() =>
                        setIsPlayerListFiltersContentOpen(!isPlayerListFiltersContentOpen)
                      }
                      onClear={resetPlayerListFiltersContent}
                      scheme="light"
                    />
                  </FilterBarWrapper>
                  {isPlayerListFiltersContentOpen && (
                    <OpenChallengeFilterContent
                      filteredTeams={playerListFilteredTeams}
                      games={games}
                      hasTeams={hasTeams}
                      onTeamsChange={togglePlayerListTeamsFilter}
                      onReset={resetPlayerListFiltersContent}
                    />
                  )}
                </PlayerListFiltersArea>
                <Responsive mobile={!selectedPlayerId} desktop>
                  <PositionBarWrapper>
                    {isRacing ? (
                      <PlayerGamesBar
                        isRacing={isRacing}
                        onSelect={handleFilter('gameId')}
                        games={games}
                        selectedGame={filters.gameId}
                        showSelectionFeedback={false}
                        showAllTab
                      />
                    ) : (
                      <PlayerPositionsBar
                        onSelect={handleFilter('positionId')}
                        positions={positions}
                        selectedPosition={filters.positionId}
                        showAllTab={showAllPositionsFilter}
                      />
                    )}
                  </PositionBarWrapper>
                </Responsive>
                <PlayerList
                  isRacing={isRacing}
                  onChange={onSelectedPlayerChange}
                  onSort={onSort}
                  players={filteredMyPlayers.filter(
                    p =>
                      playerListFilteredTeams.length === 0 ||
                      playerListFilteredTeams.includes(p.teamId)
                  )}
                  selectedPlayerId={selectedPlayerId}
                  selectedPosition={selectedPosition}
                  sortDescending={sortDescending}
                  sortField={sortField}
                  myTeam
                />
              </React.Fragment>
            )}
            {selectedGroupId && !showLineUps && !loadingSlate && (
              <Message kind="info">No available player to swap out</Message>
            )}
          </Screen>
        </Responsive>
        <Screen>
          {selectedPlayerId && (
            <React.Fragment>
              {showLineUps && (
                <PlayerPoolFiltersArea>
                  <FilterBarWrapper>
                    <StyledSearchBar
                      placeholder="Search for player to swap in"
                      onClear={() => setPlayerPoolSearchTerm('')}
                      onSearch={value => setPlayerPoolSearchTerm(value)}
                      value={playerPoolSearchTerm}
                    />
                    <StyledFiltersToggleButton
                      isFiltered={playerPoolFilteredTeams.length > 0}
                      isOpen={isPlayerPoolFiltersContentOpen}
                      onClick={() =>
                        setIsPlayerPoolFiltersContentOpen(!isPlayerPoolFiltersContentOpen)
                      }
                      onClear={resetPlayerPoolFiltersContent}
                      scheme="light"
                    />
                  </FilterBarWrapper>
                  {isPlayerPoolFiltersContentOpen && (
                    <OpenChallengeFilterContent
                      filteredTeams={playerPoolFilteredTeams}
                      games={games}
                      hasTeams={hasTeams}
                      onTeamsChange={togglePlayerPoolTeamsFilter}
                      onReset={resetPlayerPoolFiltersContent}
                    />
                  )}
                </PlayerPoolFiltersArea>
              )}
              <Responsive mobile={showLineUps && !selectedPlayerId} desktop={showLineUps}>
                <PlayerPoolPositionBarWrapper>
                  {isRacing ? gameLabel : positionName}
                </PlayerPoolPositionBarWrapper>
              </Responsive>
              <PlayerPool
                isRacing={isRacing}
                onChange={onSwapPlayerChange}
                onSort={onSort}
                players={filteredOtherPlayers.filter(
                  p =>
                    playerPoolFilteredTeams.length === 0 ||
                    playerPoolFilteredTeams.includes(p.teamId)
                )}
                selectedPosition={selectedPosition}
                sortDescending={sortDescending}
                sortField={sortField}
                value={swapPlayerId}
              />
            </React.Fragment>
          )}
        </Screen>
      </Main>
    </Wrapper>
  );
};

PlayerSwap.defaultProps = {
  isRacing: false,
  positions: [],
  selectedGroupId: undefined,
  selectedPlayerId: undefined,
  selectedPosition: undefined,
  showAllPositionsFilter: true,
  sortField: undefined,
  sportId: undefined,
  swapPlayerId: undefined
};

const connector = connect(
  (state, ownProps) => {
    const {
      hardErrorMessages,
      loadingSlate,
      loadingSlates,
      selectedGroupId,
      selectedPlayerId,
      selectedPosition,
      selectedSlate,
      slates = [],
      submitting,
      submissionData,
      swapPlayerId
    } = state.playerSwap;
    const { filterData, filters, match, sortData } = ownProps;
    const sportCategory = get(match.params.sport, state.sportCategories.items);
    const sportId = get([match.params.sport, 'filterValues', 0], state.sportCategories.items);
    const positions = get('positions', selectedSlate) || {};
    const games = (selectedSlate.games || []).filter(g => !g.isLocked);

    const positionsArray = values(positions);
    const showAllPositionsFilter = positionsArray.length > 1;
    const positionFilter = filterData.positionId;

    const groupOptions = sportCategory
      ? slates.filter(slate => {
          if (slate[sportCategory.filterKey])
            return sportCategory.filterValues.includes(slate[sportCategory.filterKey]);

          return false;
        })
      : slates;

    return {
      errorMessage: first(hardErrorMessages),
      gameLabel:
        get(
          'label',
          games.find(g => g.id === filters.gameId)
        ) || 'All',
      games,
      groupOptions: groupOptions.map(({ id, name }) => ({ label: name, value: id })),
      hasTeams: !isEmpty(selectedSlate.teams),
      isRacing: selectedSlate.isRacing,
      loadingSlate,
      loadingSlates,
      myPlayers: filterData(sortData(playerSwapMyPlayersSelector(state))),
      otherPlayers: sortData(playerSwapOtherPlayersSelector(state)),
      positionName:
        get('name', positions[selectedPosition]) ||
        get('name', positions[filters.positionId]) ||
        'All players',
      positions: positionsArray,
      selectedGroupId,
      selectedPlayerId,
      selectedPosition,
      showAllPositionsFilter,
      positionFilter,
      sportId,
      submitting,
      submissionData,
      swapPlayerId
    };
  },
  {
    onFetchSlate: getMySlate,
    onFetchSlates: getMySlates,
    onGroupChange: setChallengeGroup,
    onSelectedPlayerChange: setSelectedPlayer,
    onSwapPlayerChange: setSwapPlayer
  }
);

const enhance = compose(
  withRouter,
  withSort({ initialSortField: 'salary', initialSortDescending: true }),
  withFilter(),
  connector
);

export default enhance(PlayerSwap);

const Wrapper = styled.div(({ theme }) => ({
  color: theme.color.basis1,
  [theme.mediaQuery.desktopAndUp]: {
    padding: '1rem 0'
  }
}));

const Main = styled.div({
  display: 'flex',
  minWidth: 0
});

const Screen = styled.div(({ theme }) => ({
  flexBasis: '100%',
  flexGrow: 0,
  flexShrink: 0,
  maxWidth: '100%',
  [theme.mediaQuery.desktopAndUp]: {
    flexBasis: '50%'
  }
}));

const GroupSelect = styled(FlexiSelect)(({ theme }) => ({
  flexGrow: 1,
  [theme.mediaQuery.desktopAndUp]: {
    fontSize: '0.83rem'
  }
}));

const SummaryPanel = styled(PlayerSwapSummary)(({ theme }) => ({
  [theme.mediaQuery.desktopAndUp]: {
    marginBottom: '1rem'
  }
}));

const CalloutText = styled.div(({ danger, theme }) => ({
  color: danger ? theme.color.danger : theme.color.basis4,
  margin: '0.75rem 0',
  padding: '0 0.75rem',
  textTransform: 'uppercase'
}));

const PositionBarWrapper = styled.div(({ theme }) => ({
  [theme.mediaQuery.desktopAndUp]: {
    paddingRight: '2rem'
  }
}));

const PlayerPoolPositionBarWrapper = styled.div(({ theme }) => ({
  backgroundColor: theme.color.basis6,
  color: theme.color.basis2,
  [theme.mediaQuery.desktopAndUp]: {
    marginLeft: '2rem',
    paddingLeft: '0.75em',
    paddingRight: 0,
    paddingTop: theme.spacing.xsmall,
    paddingBottom: theme.spacing.xsmall
  }
}));

const FilterBarWrapper = styled.div({
  display: 'flex',
  margin: '2px 0'
});

const MiniFilterBarWrapper = styled.div(({ theme }) => ({
  borderTop: `1px solid ${theme.color.basis5}`,
  display: 'flex'
}));

const PositionTitle = styled.span(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.color.basis6,
  display: 'flex',
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  padding: '0.375em 0.5em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
}));

const SortButton = styled('button')({
  alignItems: 'center',
  backgroundColor: 'white',
  display: 'flex',
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  justifyContent: 'space-between',
  minWidth: 0, // flexbox hack
  padding: '0.375em 0.5em',
  textTransform: 'uppercase'
});

const PlayerSelectionMenuIcon = styled.span({
  paddingLeft: '0.5em',
  fontSize: '0.7em'
});

const StyledSearchBar = styled(SearchBar)(({ theme }) => ({
  height: '35px',
  flexBasis: '50%',
  flexGrow: 1,
  [theme.mediaQuery.mobileOnly]: {
    flexBasis: '50%',
    marginBottom: 0
  }
}));

const PlayerListFiltersArea = styled.div(({ theme }) => ({
  backgroundColor: theme.color.panel,
  fontSize: theme.fontSize.small,
  borderBottom: '2px solid',
  borderColor: theme.color.basis5,
  [theme.mediaQuery.desktopAndUp]: {
    marginLeft: '0.2ream',
    marginRight: '2rem'
  }
}));

const PlayerPoolFiltersArea = styled(PlayerListFiltersArea)(({ theme }) => ({
  backgroundColor: theme.color.panel,
  fontSize: theme.fontSize.small,
  [theme.mediaQuery.desktopAndUp]: {
    marginLeft: '2rem',
    marginRight: 0
  }
}));

const StyledFiltersToggleButton = styled(FiltersToggleButton)({
  height: '35px'
});

const SlateRow = styled.div(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing.default
}));

const StyledButton = styled(Button)({
  width: '120px'
});

const ButtonLabel = styled.span(({ theme }) => ({
  marginRight: theme.spacing.xsmall
}));

const CloseButton = styled('button')(({ theme }) => ({
  color: theme.color.basis4,
  display: 'flex',
  margin: '0 auto',
  paddingTop: theme.spacing.small,
  fontSize: '0.833rem',
  textTransform: 'uppercase',
  '&:hover': {
    color: theme.color.basis2
  }
}));
